import { useEffect, useRef, useState } from 'react';
import type { CSSProperties, ReactNode } from 'react';
import { useTranslation } from 'react-i18next';
import { EvieOrb } from './primitives';
import { useActiveSession } from '@/hooks/useActiveSession';
import { usePinnedShortcuts } from '@/hooks/usePinnedShortcuts';
import { useEvieChatStore } from '@/store/evieChat';
import styles from './QuickActions.module.css';

interface QuickActionsProps {
  onNavigate: (path: string) => void;
}

/**
 * Map-screen FAB that expands into the driver's three home-screen pins
 * (frictionless flow, piece 1) with EVII fixed at the top. The three are
 * the driver's own choice, stored on the User and read from /auth/me
 * (usePinnedShortcuts), or the defaults for what they are to the
 * network — homeowner: Start my charger / Earnings / Rewards; driver:
 * Find charger / Rewards / Friends; fleet: Start charger / My vehicle /
 * Sessions. The same three are the first row of the Menu; the picker
 * is at /shortcuts.
 *
 * While a session is in flight the home IS the session (HomeRoute) and
 * this FAB is not on screen; on /map during a session an "Active
 * session" chip is appended so the way back is one tap.
 *
 * Collapses on outside click, Escape, or after a chip is selected.
 * Positioned `fixed` in the bottom-right, above the bottom tab bar.
 * Reads exclusively from `--evi-*` tokens so it flips with the theme.
 */
export function QuickActions({ onNavigate }: QuickActionsProps) {
  const { t } = useTranslation('shortcuts');
  const [open, setOpen] = useState(false);
  const rootRef = useRef<HTMLDivElement | null>(null);
  const { pins } = usePinnedShortcuts();

  // Context-aware extra entry: a session in flight (pending, active or
  // finishing) gets a chip back to the home, which owns the live view.
  const activeSession = useActiveSession().data ?? undefined;

  useEffect(() => {
    if (!open) return;

    function onDown(e: Event): void {
      if (!rootRef.current?.contains(e.target as Node)) setOpen(false);
    }
    function onKey(e: globalThis.KeyboardEvent): void {
      if (e.key === 'Escape') setOpen(false);
    }

    document.addEventListener('mousedown', onDown);
    document.addEventListener('keydown', onKey);
    return () => {
      document.removeEventListener('mousedown', onDown);
      document.removeEventListener('keydown', onKey);
    };
  }, [open]);

  // The container uses `flex-direction: column-reverse`, so array[0] is
  // the chip *closest* to the main FAB (visually bottom) and the last
  // entry renders at the top. Reading visual top-to-bottom: pin 1, pin 2,
  // pin 3, then (only mid-session) Active session, then EVII nearest the
  // thumb. EVII opens the global sheet in place (no route): the sheet
  // lives at the AppShell level and resumes the active conversation.
  const actions: Array<{ id: string; label: string; icon: ReactNode; route?: string; action?: () => void }> = [
    { id: 'assistant', label: 'EVII™', icon: <EvieOrb size={18} />, action: () => useEvieChatStore.getState().openSheet() },
    ...(activeSession
      ? [{ id: 'active', label: t('activeSession'), icon: <LightningIcon />, route: '/' }]
      : []),
    ...[...pins].reverse().map((p) => ({ id: p.id, label: t(p.labelKey), icon: p.fabIcon ?? p.icon, route: p.href })),
  ];

  return (
    <div
      ref={rootRef}
      style={{
        position: 'fixed',
        right: 'calc(16px + env(safe-area-inset-right, 0px))',
        /* 84 bottom-nav + 48 locate button + 12 gap = 144. The live-session-bar
           offset lifts the whole stack above the MiniChargingBar when a session
           is active (the bar sets the var to 72px; 0px fallback otherwise) so
           the FAB + locate button below it are never hidden. */
        bottom: 'calc(144px + env(safe-area-inset-bottom, 0px) + var(--evi-live-session-bar-offset, 0px))',
        display: 'flex',
        flexDirection: 'column-reverse',
        alignItems: 'flex-end',
        gap: 12,
        zIndex: 30,
        // The wrapper spans a tall column when open; let its empty regions
        // (the gaps between chips) pass touches through to the map so a pinch
        // finger landing here doesn't break the gesture. The FAB and chips
        // re-enable pointer events on themselves.
        pointerEvents: 'none',
      }}
    >
      <button
        type="button"
        aria-label={open ? 'Close quick actions' : 'Open quick actions'}
        aria-expanded={open}
        onClick={() => setOpen((v) => !v)}
        className={styles.fab}
        data-tour="quick-actions"
        style={{
          width: 56,
          height: 56,
          borderRadius: '50%',
          cursor: 'pointer',
          boxShadow: '0 6px 16px rgba(0, 0, 0, 0.25)',
          display: 'inline-flex',
          alignItems: 'center',
          justifyContent: 'center',
          transition: 'transform 0.2s var(--evi-motion-fill, ease)',
          transform: open ? 'rotate(45deg)' : 'rotate(0deg)',
          pointerEvents: 'auto',
        }}
      >
        <PlusIcon />
      </button>

      {actions.map((a, i) => {
        const style: CSSProperties = {
          display: 'inline-flex',
          alignItems: 'center',
          gap: 10,
          padding: '10px 16px 10px 12px',
          borderRadius: 999,
          background: 'var(--evi-surface)',
          color: 'var(--evi-text)',
          border: '1px solid var(--evi-border)',
          cursor: 'pointer',
          fontFamily: 'inherit',
          fontSize: 13,
          fontWeight: 500,
          boxShadow: '0 4px 12px rgba(0, 0, 0, 0.2)',
          transform: open ? 'translateY(0) scale(1)' : 'translateY(12px) scale(0.9)',
          opacity: open ? 1 : 0,
          pointerEvents: open ? 'auto' : 'none',
          transitionProperty: 'transform, opacity',
          transitionDuration: '0.25s',
          transitionTimingFunction: 'var(--evi-motion-fill, cubic-bezier(0.22, 1, 0.36, 1))',
          transitionDelay: open ? `${i * 30}ms` : `${(actions.length - i - 1) * 20}ms`,
        };
        return (
          <button
            key={a.id}
            type="button"
            onClick={() => {
              setOpen(false);
              if (a.action) a.action();
              else if (a.route) onNavigate(a.route);
            }}
            style={style}
          >
            <span
              aria-hidden
              style={{
                width: 22,
                height: 22,
                display: 'inline-flex',
                alignItems: 'center',
                justifyContent: 'center',
                color: 'var(--evi-accent)',
              }}
            >
              {a.icon}
            </span>
            {a.label}
          </button>
        );
      })}
    </div>
  );
}

// ── Icons ─────────────────────────────────────────────────────────────────

function PlusIcon() {
  return (
    <svg
      viewBox="0 0 24 24"
      width="22"
      height="22"
      fill="none"
      stroke="currentColor"
      strokeWidth="2.5"
      strokeLinecap="round"
      aria-hidden
    >
      <line x1="12" y1="5" x2="12" y2="19" />
      <line x1="5" y1="12" x2="19" y2="12" />
    </svg>
  );
}

function LightningIcon() {
  return (
    <svg
      viewBox="0 0 24 24"
      width="18"
      height="18"
      fill="none"
      stroke="currentColor"
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden
    >
      <polygon points="13 2 3 14 12 14 11 22 21 10 12 10 13 2" />
    </svg>
  );
}

